import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMenu, FiX, FiPhone, FiMail } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Where We Operate', href: '/where-we-operate' },
    { name: 'Careers', href: '/careers' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-primary-900 to-secondary-900 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiPhone} className="w-4 h-4" />
                <a href="tel:03333394077" className="hover:underline">Call us: 03333 394 077</a>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiMail} className="w-4 h-4" />
                <a href="mailto:enquiries@odaracare.org.uk" className="hover:underline">enquiries@odaracare.org.uk</a>
              </div>
            </div>
            <div className="hidden md:block">
              <span>Community Centred Care</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" aria-label="KCG home">
            <img
              src="/brand/kcg-logo.svg"
              alt="KCG — Kabod Care Group"
              className="w-36 sm:w-40 h-auto"
              width="962"
              height="405"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(item.href)
                    ? 'text-primary-900 border-b-2 border-primary-900 pb-1'
                    : 'text-gray-700 hover:text-primary-900'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/request-care"
              className="bg-gradient-to-r from-primary-900 to-secondary-900 text-white px-6 py-2 rounded-full hover:from-primary-800 hover:to-secondary-800 transition-all duration-200 font-medium shadow-lg"
            >
              Start Care Journey
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-primary-900 hover:bg-primary-50"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-200"
          >
            <div className="px-4 py-6 space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block text-base font-medium transition-colors duration-200 ${
                    isActive(item.href) ? 'text-primary-900' : 'text-gray-700 hover:text-primary-900'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/request-care"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full bg-gradient-to-r from-primary-900 to-secondary-900 text-white text-center px-6 py-3 rounded-full hover:from-primary-800 hover:to-secondary-800 transition-all duration-200 font-medium"
              >
                Start Care Journey
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
